/**
 * Returns the file path without the final extension.
 *
 * @example
 * ```ts
 * filePathWithoutExtension("path/to/foo.baz") // "path/to/foo"
 * ```
 */
export function filePathWithoutExtension(filePath: string): string {
  const components = filePath.split(".");
  if (components.length === 1) {
    return filePath;
  } else {
    return components.slice(0, -1).join(".");
  }
}
/**
 * Returns the last segment (or "stem") of the file path (without the extension).
 *
 * @example
 * ```ts
 * filePathStem("path/to/foo.baz") // "foo"
 * ```
 */
export function filePathStem(filePath: string): string {
  return filePathWithoutExtension(filePath.split("/").pop() || "");
}

export function filePathExtension(filePath: string): string {
  const components = filePath.split(".");
  if (components.length === 1) {
    return "";
  } else {
    return components.pop() || "";
  }
}

export function uniqueFilePath(
  filePath: string,
  existingFilePaths: Set<string>,
): string {
  if (!existingFilePaths.has(filePath)) {
    // short circuit for most common case
    return filePath;
  }
  const baseName = filePathWithoutExtension(filePath);
  const extension = filePathExtension(filePath);

  let index: number = 2;
  const indexMatch = baseName.match(/_(\d+)$/);
  if (indexMatch && indexMatch[1]) {
    index = parseInt(indexMatch[1]) + 1;
  }

  let iterations = 0;
  do {
    filePath = `${baseName}_${index}.${extension}`;
    index++;
    iterations++;
    if (iterations > 100) {
      throw new Error(
        "Could not find a unique file path within a reasonable amount of iterations. " +
          "Stopping to prevent a potential infinite loop.",
      );
    }
  } while (existingFilePaths.has(filePath));
  return filePath;
}

/**
 * Create a new file path by appending a string between the extension (".ext")
 * and the rest of the file path.
 * @example
 *  appendToFilePath("path/to/foo.bar", " copy") // "path/to/foo copy.bar"
 */
export function appendToFileName(filePath: string, str: string): string {
  const baseName = filePathWithoutExtension(filePath);
  const extension = filePathExtension(filePath);
  return `${baseName}${str}.${extension}`;
}
